import {
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";

import {
  doc,
  getDoc
} from "firebase/firestore";

import { db } from "../utils/firebase";

export const JOB_BANK_ROLES = {
  EMPLOYER: "Employer",
  CAREER_COACH: "Career Coach",
  CANDIDATE: "Candidate",
  JOB_SEEKER: "Job Seeker",
  ADMIN: "Admin"
};

export const ACCESS_REQUEST_TYPES = {
  EMPLOYER_PROFILE: {
    requestKey: "employer-profile",
    label: "Employer Profile",
    requestedRole: JOB_BANK_ROLES.EMPLOYER,
    destination: "/employer"
  },
  CAREER_COACH_PORTAL: {
    requestKey: "career-coach-portal",
    label: "Career Coach Portal",
    requestedRole: JOB_BANK_ROLES.CAREER_COACH,
    destination: "/cecp"
  },
  HIRING_EVENT_MANAGEMENT: {
    requestKey: "hiring-event-management",
    label: "Hiring Event Management",
    requestedRole: JOB_BANK_ROLES.CAREER_COACH,
    destination: "/events"
  },
  WORKFORCE_LINKS: {
    requestKey: "workforce-links",
    label: "Workforce Links",
    requestedRole: JOB_BANK_ROLES.CAREER_COACH,
    destination: "/workforcelinks"
  }
};

const ROLE_FIELD_NAMES = [
  "jobBankRole",
  "JobBankRole",
  "jobbankRole",
  "Job Bank Role",
  "jobBank Role"
];

export function normalizeRole(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

export function getAccessRequestType(requestKey) {
  return Object.values(ACCESS_REQUEST_TYPES)
    .find(type => type.requestKey === requestKey) || null;
}

export function buildAccessRequestUrl({
  requestedRole,
  requestedAccess,
  requestKey,
  sourcePath
}) {
  const params = new URLSearchParams();

  if (requestedRole) params.set("role", requestedRole);
  if (requestedAccess) params.set("access", requestedAccess);
  if (requestKey) params.set("requestKey", requestKey);
  if (sourcePath) params.set("from", sourcePath);

  return `/accessRequest?${params.toString()}`;
}

export function requestJobBankAccess({
  requestedRole,
  requestedAccess,
  requestKey,
  sourcePath
}) {
  window.location.href = buildAccessRequestUrl({
    requestedRole,
    requestedAccess,
    requestKey,
    sourcePath: sourcePath || window.location.pathname
  });
}

export function getJobBankRoleFromUserData(data = {}) {
  for (const fieldName of ROLE_FIELD_NAMES) {
    if (data[fieldName]) return data[fieldName];
  }
  return "";
}

export function roleMatches(role, allowedRoles = []) {
  const normalizedRole = normalizeRole(role);

  if (!normalizedRole) return false;

  if (normalizedRole === normalizeRole(JOB_BANK_ROLES.ADMIN)) {
    return true;
  }

  return allowedRoles.map(normalizeRole).includes(normalizedRole);
}

export function isEmployer(role) {
  return roleMatches(role, [JOB_BANK_ROLES.EMPLOYER]);
}

export function isCareerCoach(role) {
  return roleMatches(role, [JOB_BANK_ROLES.CAREER_COACH]);
}

export function isAdmin(role) {
  return normalizeRole(role) === normalizeRole(JOB_BANK_ROLES.ADMIN);
}

export function isCandidate(role) {
  const normalized = normalizeRole(role);
  return !normalized || [
    normalizeRole(JOB_BANK_ROLES.CANDIDATE),
    normalizeRole(JOB_BANK_ROLES.JOB_SEEKER)
  ].includes(normalized);
}

export function canManageHiringEvents(role) {
  return roleMatches(role, [
    JOB_BANK_ROLES.EMPLOYER,
    JOB_BANK_ROLES.CAREER_COACH
  ]);
}

export function canManageWorkforceLinks(role) {
  return roleMatches(role, [
    JOB_BANK_ROLES.EMPLOYER,
    JOB_BANK_ROLES.CAREER_COACH
  ]);
}

export function canMessageCandidate(role) {
  return roleMatches(role, [JOB_BANK_ROLES.CAREER_COACH]);
}

export function canMessageEmployer(role) {
  return roleMatches(role, [JOB_BANK_ROLES.CAREER_COACH]);
}

export function canMessageCareerCoach(role) {
  return !!normalizeRole(role) || true;
}

export function redirectToJobBankLogin(destination) {
  sessionStorage.setItem("postLoginRedirect", destination);
  window.location.href = "/jobbank/login";
}

export function useJobBankRole(currentUser) {
  const [role, setRole] = useState("");
  const [userData, setUserData] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadRole() {
      setRoleError(null);

      if (!currentUser?.uid) {
        setRole("");
        setUserData(null);
        setRoleLoading(false);
        return;
      }

      try {
        setRoleLoading(true);

        const userRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userRef);
        const data = userDoc.exists() ? userDoc.data() : {};

        if (!isMounted) return;

        setUserData(data);
        setRole(getJobBankRoleFromUserData(data));
      } catch (error) {
        console.error("Unable to load Job Bank role:", error);

        if (isMounted) {
          setRoleError(error);
          setRole("");
          setUserData(null);
        }
      } finally {
        if (isMounted) setRoleLoading(false);
      }
    }

    loadRole();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const normalizedRole = useMemo(
    () => normalizeRole(role),
    [role]
  );

  const hasRole = useCallback(
    (allowedRoles) => roleMatches(role, allowedRoles),
    [role]
  );

  return {
    role,
    normalizedRole,
    userData,
    roleLoading,
    roleError,
    hasRole
  };
}

export function RoleDenied({
  title = "Access Restricted",
  message = "Your Job Bank role does not have access to this area.",
  requestedRole = "",
  requestedAccess = "",
  requestKey = "",
  sourcePath = "",
  allowRequest = true
}) {
  const canRequestAccess = allowRequest && requestedRole && requestedAccess;

  return (
    <div className="jobBankRoleDenied">
      <h2>{title}</h2>
      <p>{message}</p>

      {canRequestAccess && (
        <button
          onClick={() =>
            requestJobBankAccess({
              requestedRole,
              requestedAccess,
              requestKey,
              sourcePath: sourcePath || window.location.pathname
            })
          }
        >
          Request Access
        </button>
      )}

      <button onClick={() => window.location.href = "/jobbank"}>
        Return to Job Bank Home
      </button>
    </div>
  );
}
